import React from 'react';
import {Image, ImageFit, PanelType, Shimmer, Stack, Text} from '@fluentui/react';
import PanelWrapper from '../Common/PanelWrapper';
import {IWorkspaceItem} from '../../models/Workspace';
import {IProductBundleItem} from '../../models/ProductBundles';
import {INormalizedProduct} from '../../models/NormalizedProduct';
import {useGetMultipleItemDetailsQuery} from '../../store/Api';
import {
    useGetProductBundleItemsQuery,
    useGetProductBundleQuery
} from '../../store/endpoints/ProductBundleEndpoints';

interface IConfiguredBundleDetailsPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    item?: IWorkspaceItem;
}

interface IPanelRow {
    key: string;
    name: string;
    partNumber?: string;
    image?: string;
    quantity: number;
    selected: boolean;
}

interface IPanelSection {
    key: string;
    header: string;
    rows: IPanelRow[];
}

// Optionality semantics: "Required"/null/empty => required, "Optional" => optional, anything else => a group id.
const OPTIONAL = 'Optional';
const REQUIRED = 'Required';
const isGrouped = (item: IProductBundleItem) => !!item.details?.optionality && item.details.optionality !== OPTIONAL && item.details.optionality !== REQUIRED;
const isOptional = (item: IProductBundleItem) => item.details?.optionality === OPTIONAL;
const isRequired = (item: IProductBundleItem) => !item.details?.optionality || item.details.optionality === REQUIRED;

/** Selected items first, then unselected — preserving the incoming (sortOrder) order within each bucket. */
const selectedFirst = (rows: IPanelRow[]): IPanelRow[] =>
    [...rows.filter((r) => r.selected), ...rows.filter((r) => !r.selected)];

/**
 * Read-only view of the configuration a customer chose for a bundle when the order was placed.
 * Shows every item from the original bundle, sectioned by group / required / optional. Within each
 * section the selected item(s) come first; items the customer did not take are shown with quantity 0.
 * Nothing here is editable.
 */
const ConfiguredBundleDetailsPanel: React.FC<IConfiguredBundleDetailsPanelProps> = (props) => {
    const bundleId = props.item?.sourceEntityId ?? '';
    const skip = !props.isOpen || bundleId === '';

    // Prefer the order-time snapshot captured on the workspace item so the view reflects the bundle as it
    // was when ordered, even if the live bundle has since changed. Fall back to the live bundle (and only
    // then fire the live queries) for legacy items that predate the snapshot.
    const definition = props.item?.bundleDefinition;
    const hasSnapshot = !!definition && (definition.products?.length ?? 0) > 0;
    const liveSkip = skip || hasSnapshot;

    const bundleItems = useGetProductBundleItemsQuery(bundleId, {skip: liveSkip});
    const bundle = useGetProductBundleQuery(bundleId, {skip: liveSkip});

    const selections = props.item?.bundleSelections ?? [];

    // Item universe + groups: from the snapshot when present, otherwise the live bundle. Snapshot products
    // are mapped into the live IProductBundleItem shape so the section-building logic below is unchanged.
    const items: IProductBundleItem[] = React.useMemo(() => {
        if (hasSnapshot) {
            return (definition!.products ?? []).map((p, idx) => ({
                productId: p.productId ?? '',
                sortOrder: idx,
                details: {
                    quantity: p.quantity,
                    optionality: p.optionality,
                    changeQuantity: p.changeQuantity,
                    isDefault: p.isDefault,
                },
                sourceItem: {name: p.name, imageUrl: p.image},
            }));
        }
        return bundleItems.data?.items ?? [];
    }, [hasSnapshot, definition, bundleItems.data]);

    const groups = hasSnapshot
        ? (definition!.groups ?? []).map((g) => ({id: g.id, name: g.name, products: g.products ?? []}))
        : (bundle.data?.details?.productGroups ?? []);
    // Fetch live product details for every product in the bundle so the name/part number/image reflect
    // the catalog record when still available. Falls back to the snapshot's own name/image (below) for
    // products that no longer exist in the catalog. All bundle components share the org catalog.
    const catalog = items[0]?.catalog ?? props.item?.catalog ?? '';
    const productIds = items.map((i) => i.productId).filter((id): id is string => !!id);
    const productDetails = useGetMultipleItemDetailsQuery(
        {catalog, ids: productIds},
        {skip: skip || catalog === '' || productIds.length === 0}
    );
    const productsById = React.useMemo(
        () => new Map((productDetails.data ?? []).map((p) => [p.id, p])),
        [productDetails.data]
    );

    const isLoading = bundleItems.isLoading || bundle.isLoading || productDetails.isLoading;

    const buildRow = (definition: IProductBundleItem): IPanelRow => {
        const selection = selections.find((s) => s.productId === definition.productId);
        const product: INormalizedProduct | undefined =
            definition.productId ? productsById.get(definition.productId) : undefined;
        return {
            key: definition.productId ?? Math.random().toString(),
            name: product?.name ?? definition.sourceItem?.name ?? definition.productId ?? 'Unknown product',
            partNumber: product?.manufacturerPartNumber ?? definition.sourceItem?.manufacturerPartNumber,
            image: product?.image ?? definition.sourceItem?.imageUrl,
            quantity: selection?.quantity ?? 0,
            selected: !!selection,
        };
    };

    // Build the ordered sections: each group (in bundle group order) first, then required, then optional.
    const sections = React.useMemo((): IPanelSection[] => {
        const bySort = [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        const result: IPanelSection[] = [];

        // Groups — ordered by the bundle's group definition, with any stray group ids appended.
        const presentGroupIds = Array.from(
            new Set(bySort.filter(isGrouped).map((i) => i.details!.optionality!))
        );
        const orderedGroupIds = [
            ...groups.map((g) => g.id).filter((id): id is string => !!id && presentGroupIds.includes(id)),
            ...presentGroupIds.filter((id) => !groups.some((g) => g.id === id)),
        ];
        for (const groupId of orderedGroupIds) {
            const rows = selectedFirst(bySort.filter((i) => i.details?.optionality === groupId).map(buildRow));
            if (rows.length === 0) continue;
            result.push({
                key: `group-${groupId}`,
                header: groups.find((g) => g.id === groupId)?.name ?? 'Group',
                rows,
            });
        }

        // Required items after the groups.
        const requiredRows = bySort.filter(isRequired).map(buildRow);
        if (requiredRows.length > 0) result.push({key: 'required', header: 'Required', rows: requiredRows});

        // Optional items last, selected before unselected.
        const optionalRows = selectedFirst(bySort.filter(isOptional).map(buildRow));
        if (optionalRows.length > 0) result.push({key: 'optional', header: 'Optional', rows: optionalRows});

        return result;
    }, [items, groups, selections, productsById]);

    const renderRow = (row: IPanelRow) => (
        <Stack
            key={row.key}
            horizontal
            verticalAlign="center"
            tokens={{childrenGap: 12}}
            styles={{root: {borderBottom: '1px solid #eee', paddingBottom: 8, opacity: row.quantity === 0 ? 0.5 : 1}}}
        >
            {row.image &&
                <Image src={row.image} width={48} height={48} imageFit={ImageFit.contain} alt={row.name} />}
            <Stack grow>
                <Text variant="mediumPlus" styles={{root: {fontWeight: 600}}}>{row.name}</Text>
                {row.partNumber &&
                    <Text variant="small" styles={{root: {color: '#666'}}}>MPN: {row.partNumber}</Text>}
            </Stack>
            <Text variant="mediumPlus" styles={{root: {width: 40, textAlign: 'right'}}}>{row.quantity}</Text>
        </Stack>
    );

    return (
        <PanelWrapper
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            title={props.item?.adhoc?.name ?? 'Configured Bundle'}
            type={PanelType.medium}
        >
            <Stack tokens={{childrenGap: 12}} styles={{root: {padding: '8px 4px'}}}>
                <Text variant="small" styles={{root: {color: '#666'}}}>
                    The items below reflect the configuration selected by the customer when this bundle was ordered.
                </Text>

                {isLoading && <Shimmer />}

                {!isLoading && sections.length === 0 &&
                    <Text>This bundle has no items.</Text>}

                {!isLoading && sections.map((section, index) => (
                    <Stack key={section.key} tokens={{childrenGap: 8}}>
                        <Stack
                            horizontal
                            verticalAlign="center"
                            tokens={{childrenGap: 12}}
                            styles={{root: {borderBottom: '2px solid #000000', paddingBottom: 2}}}
                        >
                            <Stack grow>
                                <Text variant="mediumPlus" styles={{root: {fontWeight: 700, color: '#000000'}}}>
                                    {section.header}
                                </Text>
                            </Stack>
                            {index === 0 &&
                                <Text variant="mediumPlus" styles={{root: {fontWeight: 700, color: '#000000', width: 40, textAlign: 'right'}}}>
                                    Qty
                                </Text>}
                        </Stack>
                        {section.rows.map(renderRow)}
                    </Stack>
                ))}
            </Stack>
        </PanelWrapper>
    );
};

export default ConfiguredBundleDetailsPanel;
